import {
  ChatBubbleLeftEllipsisIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/16/solid"
import { ArrowDownCircleIcon } from "@heroicons/react/20/solid"
import { QrCodeIcon } from "@heroicons/react/24/outline"
import {
  CARD_SURFACE_CLASSES,
  ExploreItem,
  type ExploreItem as ExploreItemData,
  INTERACTIVE_CARD_OUTLINE_CLASSES,
} from "@src/components/ExploreItem"
import { PromotionExploreItem } from "@src/features/promotions/components/PromotionExploreItem"
import { NearNIcon } from "@src/icons"
import { useAddressModal } from "@src/providers/AddressModalProvider"
import { useConfidentialMode } from "@src/providers/ConfidentialModeProvider"
import { FeatureFlagsContext } from "@src/providers/FeatureFlagsProvider"
import clsx from "clsx"
import { useContext, useState } from "react"
import ConfidentialSwapIntroModal from "./ConfidentialSwapIntroModal"

const confidentialModeItems: ExploreItemData[] = [
  {
    href: "/send/confidential",
    title: "Confidential payments",
    description:
      "Send tokens without exposing your wallet activity or transaction history.",
    illustration: (
      <div className="flex w-52 flex-col gap-0.5">
        <div className="flex items-center justify-between rounded-lg bg-white px-4 py-2 dark:bg-gray-850">
          <div className="size-4 shrink-0 rounded-full bg-green-500" />
          <div className="h-2 w-10 rounded-xs bg-gray-150 dark:bg-white/10" />
        </div>

        <div className="flex items-center justify-between rounded-t-lg bg-white p-4 dark:bg-gray-850">
          <div className="-mt-0.5 text-[3.5rem]/3 text-gray-500 tracking-tighter">
            &middot;&middot;&middot;&middot;&middot;
          </div>
          <div className="h-2 w-10 rounded-xs bg-gray-150 dark:bg-white/10" />
        </div>
      </div>
    ),
  },
  {
    href: "/receive",
    title: "Confidential deposits",
    description: "Receive tokens directly to your Confidential account.",
    illustration: (
      <div className="flex w-52 flex-col gap-4">
        <div className="flex items-center justify-center gap-2">
          <div className="size-3 shrink-0 rounded-full bg-white/10" />
          <div className="h-1 w-6 shrink-0 rounded-[1px] bg-white/10" />
          <div className="size-3 shrink-0 rounded-full bg-white/10" />
          <div className="h-1 w-6 shrink-0 rounded-[1px] bg-white/10" />
          <div className="size-3 shrink-0 rounded-full bg-green-500" />
        </div>

        <div className="rounded-t-lg bg-white px-4 py-3 dark:bg-gray-850">
          <div className="flex items-center justify-between">
            <div className="h-3 w-20 rounded-sm bg-gray-150 dark:bg-white/10" />
            <QrCodeIcon className="size-6 shrink-0 text-gray-500" />
          </div>
        </div>
      </div>
    ),
  },
]

const Explore = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const { confidentialMode } = useConfidentialMode()

  const { isPromotionsDisabled, isReferralDisabled } =
    useContext(FeatureFlagsContext)

  const items: ExploreItemData[] = [
    {
      title: "Confidential swaps",
      description:
        "Swap without exposing your balances or transaction details onchain.",
      onClick: () => setModalOpen(true),
      illustration: (
        <div className="relative flex w-52 shrink-0 flex-col gap-0.5">
          <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 dark:bg-gray-850">
            <div className="text-4xl/4 text-gray-500 tracking-tighter">
              &middot;&middot;&middot;&middot;&middot;&middot;
            </div>

            <div className="flex items-center gap-2">
              <div className="h-2 w-6 shrink-0 rounded-xs bg-gray-150 dark:bg-white/10" />
              <div className="size-4 shrink-0 rounded-full bg-green-500" />
            </div>
          </div>

          <div className="-translate-x-1/2 -translate-y-1/2 -outline-offset-3 absolute top-1/2 left-1/2 flex size-5 items-center justify-center rounded-full bg-white text-gray-800 outline-4 outline-gray-800 dark:text-gray-900 dark:outline-gray-900">
            <ArrowDownCircleIcon className="size-5" />
          </div>

          <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 dark:bg-gray-850">
            <div className="text-4xl/4 text-gray-500 tracking-tighter">
              &middot;&middot;&middot;&middot;&middot;&middot;
            </div>

            <div className="flex items-center gap-2">
              <div className="h-2 w-6 shrink-0 rounded-xs bg-gray-150 dark:bg-white/10" />
              <div className="size-4 shrink-0 rounded-full bg-gray-150 dark:bg-white/10" />
            </div>
          </div>
        </div>
      ),
    },
    {
      href: "/gifts/new",
      title: "Give a gift",
      description:
        "Surprise someone with a gift, helping them get started with near.com!",
      illustration: (
        <div className="relative w-52">
          <div className="absolute inset-0 rounded-xl bg-linear-to-tr from-green-600 via-green-400 to-green-500 opacity-10 blur-2xl" />
          <div className="-outline-offset-1 relative flex aspect-[1.586/1] w-full flex-col items-start justify-between overflow-hidden rounded-xl bg-linear-to-tr from-green-600 via-green-400 to-green-500 p-4 outline outline-white/30">
            <div className="mask-[url('/static/images/near-star-tile.svg')] mask-size-[8px_8px] mask-repeat absolute inset-0 bg-linear-to-b from-black/10 to-70% to-black/0" />
            <NearNIcon className="relative size-4 text-black/80" />
          </div>
        </div>
      ),
    },
  ]

  const referralItem: ExploreItemData = {
    href: "/referrals",
    title: "Invite friends, earn rewards",
    description: "Share your link with friends and earn when they trade.",
    illustration: (
      <div className="flex w-52 shrink-0 flex-col gap-4">
        <div className="flex gap-4 px-4">
          <div className="h-3 flex-1 rounded-sm bg-white/10" />
          <div className="h-3 w-10 rounded-sm bg-green-500" />
        </div>

        <div className="flex flex-col gap-2 rounded-t-lg bg-white px-4 py-3 dark:bg-gray-850">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="size-4 shrink-0 rounded-full bg-gray-150 dark:bg-white/10" />
              <div className="h-2 flex-1 rounded-xs bg-gray-150 dark:bg-white/10" />
              <div className="h-2 w-10 shrink-0 rounded-xs bg-gray-150 dark:bg-white/10" />
            </div>
          ))}
        </div>
      </div>
    ),
  }

  // Keep the layout at three cards while preserving both contextual
  // Confidential actions. Referral takes priority only in the regular mode.
  const renderExploreItem = (item: ExploreItemData) => (
    <ExploreItem key={item.title} item={item} />
  )

  const regularItems = confidentialMode
    ? [...confidentialModeItems, ...items]
    : [...(!isReferralDisabled ? [referralItem] : []), ...items]

  const exploreItems = [
    ...(!isPromotionsDisabled
      ? [<PromotionExploreItem key="promotion" />]
      : []),
    ...regularItems.map(renderExploreItem),
  ].slice(0, 3)

  return (
    <section className="space-y-10 sm:space-y-16">
      <div className="space-y-4">
        <h2 className="font-semibold text-gray-900 text-xl/6 tracking-tight dark:text-white">
          Explore
        </h2>

        <div className="hide-scrollbar -mx-4 sm:-mx-6 flex snap-x snap-mandatory overflow-x-auto px-4 sm:px-6 min-[900px]:mx-0 min-[900px]:overflow-x-visible min-[900px]:px-0">
          <div className="mx-auto flex flex-1 gap-3 min-[900px]:grid min-[900px]:grid-cols-3">
            {exploreItems}
          </div>
        </div>
      </div>

      <AdditionalActions />

      <ConfidentialSwapIntroModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </section>
  )
}

const AdditionalActions = () => {
  const { openAddressModal } = useAddressModal()

  const actions = [
    {
      title: "Share contact",
      subtitle: "Receive from other near.com users",
      icon: QrCodeIcon,
      onClick: openAddressModal,
    },
    {
      title: "Help articles",
      subtitle: "Find answers to common questions",
      icon: QuestionMarkCircleIcon,
      onClick: () => {
        if (typeof window !== "undefined" && window.Beacon) {
          window.Beacon("navigate", "/answers/")
          window.Beacon("open")
        }
      },
    },
    {
      title: "Contact support",
      subtitle: "We usually respond within 24 hours",
      icon: ChatBubbleLeftEllipsisIcon,
      onClick: () => {
        if (typeof window !== "undefined" && window.Beacon) {
          window.Beacon("navigate", "/ask/message/")
          window.Beacon("open")
        }
      },
    },
  ]

  return (
    <section className="grid gap-3 md:grid-cols-3">
      {actions.map(({ title, subtitle, icon: Icon, onClick }) => (
        <button
          key={title}
          type="button"
          onClick={onClick}
          className={clsx(
            CARD_SURFACE_CLASSES,
            INTERACTIVE_CARD_OUTLINE_CLASSES,
            "flex items-start justify-center gap-3 rounded-2xl p-4"
          )}
        >
          <div className="flex h-6 items-center justify-center">
            <Icon className="size-5 text-gray-400 dark:text-gray-500" />
          </div>

          <div className="flex flex-1 flex-col items-start gap-0.5">
            <span className="font-semibold text-base/6 text-gray-900 tracking-tight dark:text-white">
              {title}
            </span>
            <span className="font-medium text-gray-500 text-sm/5 dark:text-gray-400">
              {subtitle}
            </span>
          </div>
        </button>
      ))}
    </section>
  )
}

export default Explore
